import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  ScaleIcon,
  PlayIcon,
  StopIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const SAMPLE_SIZE_OPTIONS = [5000, 10000, 25000];

const ACTIVE_STATUSES = ['pending', 'running', 'stopping'];

function fmt(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const num = Number(n);
  if (Math.abs(num) >= 1000) return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPct(n) {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return `${Number(n).toFixed(1)}%`;
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-2xl font-bold tabular-nums text-gray-900 dark:text-white">{value}</div>
      {sub && <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{sub}</div>}
    </div>
  );
}

function ComparisonRow({ label, followback, notYet, diffLabel, isPct = false }) {
  const fmtVal = isPct ? fmtPct : fmt;
  return (
    <tr className="border-b border-gray-100 dark:border-gray-700 last:border-0">
      <td className="py-2 pr-4 text-sm text-gray-600 dark:text-gray-300">{label}</td>
      <td className="py-2 pr-4 text-sm font-medium text-emerald-600 dark:text-emerald-400 tabular-nums">{fmtVal(followback)}</td>
      <td className="py-2 pr-4 text-sm font-medium text-slate-500 dark:text-slate-400 tabular-nums">{fmtVal(notYet)}</td>
      <td className="py-2 text-sm tabular-nums text-gray-500 dark:text-gray-400">{diffLabel}</td>
    </tr>
  );
}

function bucketChartData(followbackBuckets = {}, notYetBuckets = {}, order) {
  const keys = order || Array.from(new Set([...Object.keys(followbackBuckets), ...Object.keys(notYetBuckets)]));
  return {
    labels: keys,
    datasets: [
      {
        label: 'Followed back',
        data: keys.map((k) => followbackBuckets[k] || 0),
        backgroundColor: 'rgba(16, 185, 129, 0.75)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
        borderRadius: 4,
      },
      {
        label: 'Not yet followed back',
        data: keys.map((k) => notYetBuckets[k] || 0),
        backgroundColor: 'rgba(148, 163, 184, 0.75)',
        borderColor: 'rgb(100, 116, 139)',
        borderWidth: 1,
        borderRadius: 4,
      },
    ],
  };
}

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { position: 'bottom' } },
  scales: { y: { beginAtZero: true } },
};

const FollowbackProfileComparison = () => {
  const [sampleSize, setSampleSize] = useState(5000);
  const [censusFollowbacks, setCensusFollowbacks] = useState(false);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [report, setReport] = useState(null);
  const [starting, setStarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const res = await api.get('/admin/followback-comparison/jobs');
      const list = res.data?.jobs || [];
      setJobs(list);
      const active = list.find((j) => ACTIVE_STATUSES.includes(j.status));
      if (active) {
        setJob(active);
      } else if (!job && list.length) {
        setJob(list[0]);
      }
    } catch (err) {
      // silent — page still usable to start a new job
    }
  }, [job]);

  useEffect(() => {
    loadJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pollJob = useCallback(async (jobId) => {
    try {
      const res = await api.get(`/admin/followback-comparison/jobs/${jobId}`);
      const freshJob = res.data?.job;
      if (freshJob) setJob(freshJob);
      return freshJob;
    } catch (err) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (job && ACTIVE_STATUSES.includes(job.status)) {
      pollRef.current = setInterval(async () => {
        const fresh = await pollJob(job._id);
        if (fresh && !ACTIVE_STATUSES.includes(fresh.status)) {
          clearInterval(pollRef.current);
          loadJobs();
        }
      }, 5000);
      return () => clearInterval(pollRef.current);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?._id, job?.status]);

  useEffect(() => {
    if (job?.status === 'completed') {
      setLoadingReport(true);
      api.get(`/admin/followback-comparison/jobs/${job._id}/results`)
        .then((res) => setReport(res.data?.report || null))
        .catch(() => setReport(null))
        .finally(() => setLoadingReport(false));
    } else {
      setReport(null);
    }
  }, [job?._id, job?.status]);

  const hasActiveJob = job && ACTIVE_STATUSES.includes(job.status);

  const handleStart = async () => {
    setStarting(true);
    try {
      const res = await api.post('/admin/followback-comparison/jobs', censusFollowbacks
        ? {
            sampleSize: 200000,
            censusFollowbacks: true,
            controlMultiplier: 2,
            poolSize: 5000,
            workerConcurrency: 200,
          }
        : { sampleSize });
      const newJob = res.data?.job;
      if (newJob) {
        setJob(newJob);
        toast.success(
          censusFollowbacks
            ? 'Census comparison started (all follow-backs vs 2× control, 200 checkers)'
            : `Comparison job started (${sampleSize.toLocaleString()} per group)`
        );
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start comparison job');
    } finally {
      setStarting(false);
    }
  };

  const handleStop = async () => {
    if (!job) return;
    setStopping(true);
    try {
      await api.post(`/admin/followback-comparison/jobs/${job._id}/stop`);
      toast.success('Stopping job…');
      pollJob(job._id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to stop job');
    } finally {
      setStopping(false);
    }
  };

  const progress = job?.progress || {};
  const totalUsernames = progress.totalUsernames || 0;
  const processed = progress.processed || 0;
  const pct = totalUsernames > 0 ? Math.min(100, Math.round((processed / totalUsernames) * 100)) : 0;

  const followbackProgress = progress.byGroup?.followback || {};
  const notYetProgress = progress.byGroup?.not_yet_followed_back || {};

  const statusLabel = useMemo(() => {
    if (!job) return null;
    switch (job.status) {
      case 'pending': return 'Queued…';
      case 'running': return `Running — fetching X profile data (${pct}%)`;
      case 'stopping': return 'Stopping…';
      case 'stopped': return 'Stopped';
      case 'completed': return 'Completed';
      case 'failed': return `Failed — ${job.lastError || 'unknown error'}`;
      default: return job.status;
    }
  }, [job, pct]);

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 shadow-lg shadow-indigo-500/20">
          <ScaleIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Followback Profile Comparison</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Compare followed-back accounts against a cohort-matched sample of accounts that haven't followed back yet —
            profile, verification, and recent-activity signals pulled live from X.
          </p>
        </div>
      </div>

      {/* Config / start */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
              Sample size per group
            </label>
            <select
              value={sampleSize}
              onChange={(e) => setSampleSize(Number(e.target.value))}
              disabled={hasActiveJob || censusFollowbacks}
              className="form-select text-sm w-48"
            >
              {SAMPLE_SIZE_OPTIONS.map((size) => (
                <option key={size} value={size}>{size.toLocaleString()} per group</option>
              ))}
            </select>
            <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={censusFollowbacks}
                disabled={hasActiveJob}
                onChange={(e) => setCensusFollowbacks(e.target.checked)}
              />
              All unique follow-backs vs 2× control (200 checkers)
            </label>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 flex-1">
            {censusFollowbacks
              ? 'Census of every unique follow-back against twice as many week-matched never-followed-back accounts. Rotates every eligible checker. Expect ~1–3 hours.'
              : 'Uses an isolated slice of active_checker accounts. Estimated runtime: 5,000 → 5-20 min · 10,000 → 10-40 min · 25,000 → 25-100 min. Recommend validating with 5,000 first.'}
          </p>
          <div className="flex gap-2">
            {!hasActiveJob ? (
              <button
                type="button"
                onClick={handleStart}
                disabled={starting}
                className="btn-primary text-sm flex items-center gap-1.5 whitespace-nowrap"
              >
                <PlayIcon className="w-4 h-4" />
                {starting ? 'Starting…' : 'Start comparison'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleStop}
                disabled={stopping || job.status === 'stopping'}
                className="btn-secondary text-sm flex items-center gap-1.5 whitespace-nowrap text-red-600 dark:text-red-400"
              >
                <StopIcon className="w-4 h-4" />
                {stopping || job.status === 'stopping' ? 'Stopping…' : 'Stop job'}
              </button>
            )}
            <button
              type="button"
              onClick={loadJobs}
              className="btn-secondary text-sm flex items-center gap-1.5"
              title="Refresh"
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Progress */}
      {job && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{statusLabel}</h2>
            <span className="text-xs text-gray-400 tabular-nums">
              {processed.toLocaleString()} / {totalUsernames.toLocaleString()}
            </span>
          </div>
          {ACTIVE_STATUSES.includes(job.status) && (
            <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all duration-500 ease-out"
                style={{ width: `${pct}%` }}
              />
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniStat label="Followback processed" value={fmt(followbackProgress.processed)} sub={`of ${fmt(followbackProgress.total)}`} />
            <MiniStat label="Not-yet processed" value={fmt(notYetProgress.processed)} sub={`of ${fmt(notYetProgress.total)}`} />
            <MiniStat label="Succeeded" value={fmt(progress.succeeded)} />
            <MiniStat label="Failed" value={fmt(progress.failed)} />
          </div>
          {progress.currentUsername && (
            <p className="text-xs text-gray-400">Last processed: @{progress.currentUsername}</p>
          )}
        </div>
      )}

      {/* Report */}
      {job?.status === 'completed' && (
        <div className="space-y-6">
          {loadingReport && <p className="text-sm text-gray-500">Loading report…</p>}
          {report && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <MiniStat label="Followback sample" value={fmt(report.followback?.sampleCount)} />
                <MiniStat label="Not-yet sample" value={fmt(report.notYet?.sampleCount)} />
                <MiniStat
                  label="Avg following/followers ratio"
                  value={fmt(report.followback?.followingToFollowersRatio?.avg)}
                  sub={`vs ${fmt(report.notYet?.followingToFollowersRatio?.avg)} not-yet`}
                />
                <MiniStat
                  label="Avg days since last tweet"
                  value={fmt(report.followback?.daysSinceLastTweet?.avg)}
                  sub={`vs ${fmt(report.notYet?.daysSinceLastTweet?.avg)} not-yet`}
                />
              </div>

              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 overflow-x-auto">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Metric comparison</h3>
                <table className="w-full min-w-[500px]">
                  <thead>
                    <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700">
                      <th className="pb-2">Metric</th>
                      <th className="pb-2">Followed back</th>
                      <th className="pb-2">Not yet</th>
                      <th className="pb-2">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ComparisonRow label="Followers (avg)" followback={report.followback?.followersCount?.avg} notYet={report.notYet?.followersCount?.avg} diffLabel={fmt(report.delta?.followersCount?.diff)} />
                    <ComparisonRow label="Following (avg)" followback={report.followback?.followingCount?.avg} notYet={report.notYet?.followingCount?.avg} diffLabel={fmt(report.delta?.followingCount?.diff)} />
                    <ComparisonRow label="Following/followers ratio" followback={report.followback?.followingToFollowersRatio?.avg} notYet={report.notYet?.followingToFollowersRatio?.avg} diffLabel={fmt(report.delta?.followingToFollowersRatio?.diff)} />
                    <ComparisonRow label="Favourites given (avg)" followback={report.followback?.favouritesCount?.avg} notYet={report.notYet?.favouritesCount?.avg} diffLabel={fmt(report.delta?.favouritesCount?.diff)} />
                    <ComparisonRow label="Tweets (avg)" followback={report.followback?.statusesCount?.avg} notYet={report.notYet?.statusesCount?.avg} diffLabel={fmt(report.delta?.statusesCount?.diff)} />
                    <ComparisonRow label="Media tweets (avg)" followback={report.followback?.mediaCount?.avg} notYet={report.notYet?.mediaCount?.avg} diffLabel={fmt(report.delta?.mediaCount?.diff)} />
                    <ComparisonRow label="Username changes (avg)" followback={report.followback?.usernameChangesCount?.avg} notYet={report.notYet?.usernameChangesCount?.avg} diffLabel={fmt(report.delta?.usernameChangesCount?.diff)} />
                    <ComparisonRow label="Recent avg likes/tweet" followback={report.followback?.recentAvgFavoriteCount?.avg} notYet={report.notYet?.recentAvgFavoriteCount?.avg} diffLabel={fmt(report.delta?.recentAvgFavoriteCount?.diff)} />
                    <ComparisonRow label="Recent avg retweets/tweet" followback={report.followback?.recentAvgRetweetCount?.avg} notYet={report.notYet?.recentAvgRetweetCount?.avg} diffLabel={fmt(report.delta?.recentAvgRetweetCount?.diff)} />
                    <ComparisonRow label="Recent avg views/tweet" followback={report.followback?.recentAvgViewsCount?.avg} notYet={report.notYet?.recentAvgViewsCount?.avg} diffLabel={fmt(report.delta?.recentAvgViewsCount?.diff)} />
                    <ComparisonRow label="Blue verified" followback={report.followback?.pctBlueVerified} notYet={report.notYet?.pctBlueVerified} diffLabel={fmtPct(report.delta?.pctBlueVerified?.diff)} isPct />
                    <ComparisonRow label="Verified" followback={report.followback?.pctVerified} notYet={report.notYet?.pctVerified} diffLabel={fmtPct(report.delta?.pctVerified?.diff)} isPct />
                    <ComparisonRow label="Protected" followback={report.followback?.pctProtected} notYet={report.notYet?.pctProtected} diffLabel={fmtPct(report.delta?.pctProtected?.diff)} isPct />
                    <ComparisonRow label="Graduated access" followback={report.followback?.pctHasGraduatedAccess} notYet={report.notYet?.pctHasGraduatedAccess} diffLabel={fmtPct(report.delta?.pctHasGraduatedAccess?.diff)} isPct />
                    <ComparisonRow label="Can DM" followback={report.followback?.pctCanDm} notYet={report.notYet?.pctCanDm} diffLabel={fmtPct(report.delta?.pctCanDm?.diff)} isPct />
                    <ComparisonRow label="Possibly sensitive" followback={report.followback?.pctPossiblySensitive} notYet={report.notYet?.pctPossiblySensitive} diffLabel={fmtPct(report.delta?.pctPossiblySensitive?.diff)} isPct />
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5" style={{ height: 320 }}>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Follower size buckets</h3>
                  <div style={{ height: 250 }}>
                    <Bar
                      data={bucketChartData(report.followback?.followerSizeBuckets, report.notYet?.followerSizeBuckets, ['<1k', '1k-10k', '10k-50k', '50k+'])}
                      options={chartOptions}
                    />
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5" style={{ height: 320 }}>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Account age buckets</h3>
                  <div style={{ height: 250 }}>
                    <Bar
                      data={bucketChartData(report.followback?.accountAgeBuckets, report.notYet?.accountAgeBuckets, ['<30d', '1-6mo', '6-12mo', '>1yr'])}
                      options={chartOptions}
                    />
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5" style={{ height: 320 }}>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent activity recency</h3>
                  <div style={{ height: 250 }}>
                    <Bar
                      data={bucketChartData(report.followback?.recencyBuckets, report.notYet?.recencyBuckets, ['24h', '7d', '30d', 'older', 'unknown'])}
                      options={chartOptions}
                    />
                  </div>
                </div>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5" style={{ height: 320 }}>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Top countries (account based in)</h3>
                  <div style={{ height: 250 }}>
                    <Bar
                      data={bucketChartData(
                        Object.fromEntries((report.followback?.topCountries || []).map((c) => [c.key, c.count])),
                        Object.fromEntries((report.notYet?.topCountries || []).map((c) => [c.key, c.count]))
                      )}
                      options={chartOptions}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Job history */}
      {jobs.length > 0 && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 overflow-x-auto">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">Recent runs</h3>
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700">
                <th className="pb-2">Started</th>
                <th className="pb-2">Sample size</th>
                <th className="pb-2">Status</th>
                <th className="pb-2">Processed</th>
                <th className="pb-2" />
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j._id} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <td className="py-2 pr-4 text-sm text-gray-600 dark:text-gray-300">
                    {j.startedAt ? new Date(j.startedAt).toLocaleString() : new Date(j.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-sm tabular-nums text-gray-600 dark:text-gray-300">
                    {j.config?.sampleSize?.toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 text-sm text-gray-600 dark:text-gray-300 capitalize">{j.status}</td>
                  <td className="py-2 pr-4 text-sm tabular-nums text-gray-600 dark:text-gray-300">
                    {fmt(j.progress?.processed)} / {fmt(j.progress?.totalUsernames)}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setJob(j)}
                      className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default FollowbackProfileComparison;
